import { useState } from "react";
import { Link } from "react-router-dom";
import NetworkGraph from "../../components/NetworkGraph";
import Reveal from "../../components/Reveal";
import { IconHeadset } from "../../components/Icons";

const ACCENT = "#0EA5E9";

const FAQS = [
  {
    question: "Qual o horário de atendimento?",
    answer:
      "Nosso atendimento comercial é de segunda a sexta, das 8h30 às 17h30. Para clientes com contrato de gestão MSP, monitoramentos críticos são tratados fora do horário conforme o SLA contratado.",
  },
  {
    question: "Quanto tempo leva para atender um chamado?",
    answer:
      "O SLA é definido em contrato. Chamados críticos (sistema fora do ar, empresa parada) têm prioridade máxima. O tempo de resposta e resolução é acordado antes da contratação.",
  },
  {
    question: "Posso abrir chamado para qualquer tipo de problema?",
    answer:
      "Sim. Problemas com computador, impressora, rede, e-mail, sistemas, acesso, lentidão — qualquer coisa que afete a operação da sua empresa pode ser acionada via chamado.",
  },
  {
    question: "O suporte presencial tem custo adicional?",
    answer:
      "Para clientes com contrato de gestão MSP, o suporte presencial dentro do escopo contratado não tem custo extra. Detalhes são definidos na proposta.",
  },
];

const FEATURES = [
  {
    title: "Abertura de Chamados",
    desc: "Portal dedicado para abertura e acompanhamento de chamados com histórico completo",
  },
  {
    title: "Atendimento Remoto",
    desc: "Resolução remota ágil pela maioria das solicitações — sem precisar esperar uma visita",
  },
  {
    title: "Atendimento Presencial em SP",
    desc: "Quando necessário, nossa equipe vai até o seu escritório em São Paulo",
  },
  {
    title: "SLA Definido",
    desc: "Tempo de resposta e resolução definidos em contrato, sem deixar você na mão",
  },
  {
    title: "Suporte ao Usuário Final",
    desc: "Problemas do dia a dia dos colaboradores — e-mail, impressora, acesso, lentidão",
  },
  {
    title: "Registro e Relatórios",
    desc: "Todo chamado registrado, com histórico e relatório mensal de atendimentos",
  },
];

const STEPS = [
  {
    title: "Abra o chamado",
    desc: "Via portal, e-mail ou telefone",
  },
  {
    title: "Análise e triagem",
    desc: "Nossa equipe avalia prioridade e designa o técnico",
  },
  {
    title: "Atendimento remoto",
    desc: "Resolução imediata para a maioria dos chamados",
  },
  {
    title: "Presencial se necessário",
    desc: "Agendamos visita ao seu escritório em São Paulo",
  },
  {
    title: "Fechamento e registro",
    desc: "Chamado encerrado com solução registrada no histórico",
  },
];

/* =========================================================================
   Níveis de suporte por plano (ST 1 a ST 4)
   Reflete os níveis de atendimento — Remoto N1, N2, N3 e In Loco — cobertos
   por cada plano. Os nomes dos planos seguem exatamente os da planilha de
   referência (ST 3 e ST 4 aparecem como "Usuário doméstico").
   ========================================================================= */

const SUPPORT_TIERS = [
  { id: "st1", label: "ST 1", name: "Usuário doméstico" },
  { id: "st2", label: "ST 2", name: "Usuário Home Office" },
  { id: "st3", label: "ST 3", name: "Usuário doméstico" },
  { id: "st4", label: "ST 4", name: "Usuário doméstico" },
];

const SUPPORT_LEVELS = [
  {
    title: "Suporte de Nível 1 — Remoto",
    items: [
      { label: "Reparo do pacote Office", flags: [1, 1, 1, 1] },
      { label: "Instalação/Configuração/Reparo Conexão Client VPN", flags: [1, 1, 1, 1] },
      { label: "Auxílio em reparo navegação como sites GOV", flags: [1, 1, 1, 1] },
      { label: "Limpeza de cache em navegadores", flags: [1, 1, 1, 1] },
      { label: "Verificação de vírus", flags: [1, 1, 1, 1] },
      { label: "Solução de problemas com internet (exceto operadora)", flags: [1, 1, 1, 1] },
      { label: "Configuração de impressora e outros periféricos", flags: [1, 1, 1, 1] },
      { label: "Instalação e atualização de drivers", flags: [1, 1, 1, 1] },
      { label: "Reparo ou atualização do Java", flags: [1, 1, 1, 1] },
    ],
  },
  {
    title: "Suporte de Nível 2",
    items: [
      { label: "Instalações de Software Padrões (Office, Navegador)", flags: [0, 1, 1, 1] },
      { label: "Backup do Outlook (PST)", flags: [0, 1, 1, 1] },
      { label: "Instalação/Configuração/Reparo de Certificados Digitais", flags: [0, 1, 1, 1] },
      { label: "Otimização do sistema", flags: [0, 1, 1, 1] },
    ],
  },
  {
    title: "Suporte de Nível 3",
    note: "Requer nível de acesso total ao seu parque tecnológico, in loco ou remoto.",
    items: [
      { label: "Instalação de Softwares Avançados (Photoshop/CorelDRAW) — licença fornecida pelo cliente", flags: [0, 0, 1, 1] },
      { label: "Instalação de Softwares de terceiros (Vmoney/WBA)", flags: [0, 0, 1, 1] },
      { label: "Gestão de painel de e-mail", flags: [0, 0, 1, 1] },
      { label: "Gestão de painel de AntiVírus", flags: [0, 0, 1, 1] },
      { label: "Gestão e Administração de Servidor", flags: [0, 0, 1, 1] },
      { label: "Configuração de novo perfil de usuário", flags: [0, 0, 1, 1] },
    ],
  },
  {
    title: "Suporte In Loco",
    items: [
      { label: "Gestão e Solução de Ambiente", flags: [0, 0, 0, 1] },
    ],
  },
];

/* Achata os grupos acima em linhas de tabela (cabeçalho de grupo + itens),
   já com chaves únicas, para renderizar num único map sem precisar de
   React.Fragment. */
const SUPPORT_ROWS = SUPPORT_LEVELS.flatMap((group, gi) => [
  { type: "group", key: `g-${gi}`, title: group.title, note: group.note },
  ...group.items.map((item, ii) => ({ type: "item", key: `g-${gi}-i-${ii}`, ...item })),
]);

export default function Suporte() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <Link to="/servicos/servicos">Serviços</Link>
            <span>/</span>
            <span>Suporte Técnico</span>
          </div>
          <div className="tag animate-up animate-up--d1" style={{ color: ACCENT, background: "rgba(14,165,233,.12)", borderColor: `${ACCENT}40` }}>
            <IconHeadset style={{ width: "13px", height: "13px" }} />
            Helpdesk · Remoto &amp; Presencial
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px,5vw,64px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.1",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            Suporte Técnico
            <br />
            <span style={{ color: ACCENT }}>
              Quando você precisa, estamos lá
            </span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "560px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Atendimento via chamado, remoto e presencial em São Paulo.
            Equipe técnica que resolve problemas de verdade — sem enrolação,
            sem você ficar na fila.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Solicitar proposta →
            </Link>
            <Link to="/servicos/servicos" className="btn btn-outline">
              Ver todos os serviços
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ O QUE ESTÁ INCLUSO ═══════════════ */}
      <section className="section">
        <div className="container">
          <div className="grid-2col">
            <Reveal>
              <div className="tag" style={{ color: ACCENT, background: "rgba(14,165,233,.12)", borderColor: `${ACCENT}40` }}>
                O que está incluso
              </div>
              <h2 className="section-title" style={{ marginBottom: "28px" }}>
                O que a sepolTECH <span style={{ color: ACCENT }}>entrega</span>
              </h2>
              <div className="feature-list">
                {FEATURES.map((f) => (
                  <div className="feature-item" key={f.title}>
                    <div
                      className="feature-item__icon"
                      style={{ background: "rgba(14,165,233,.15)", color: ACCENT }}
                    >
                      ✓
                    </div>
                    <div>
                      <strong style={{ color: "var(--text-primary)" }}>
                        {f.title}
                      </strong>{" "}
                      — {f.desc}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="card" style={{ padding: "32px" }}>
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "18px",
                    marginBottom: "20px",
                    color: "var(--text-primary)",
                  }}
                >
                  Como funciona o atendimento
                </h3>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: "20px" }}
                >
                  {STEPS.map((step, index) => (
                    <div
                      key={step.title}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          background: "rgba(14,165,233,.15)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: ACCENT,
                          fontFamily: "var(--font-mono)",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <strong style={{ display: "block", marginBottom: "4px", color: "var(--text-primary)" }}>
                          {step.title}
                        </strong>
                        <span
                          style={{ fontSize: "14px", color: "var(--text-muted)" }}
                        >
                          {step.desc}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════ NÍVEIS DE SUPORTE POR PLANO ═══════════════ */}
      <section id="suporte" className="section section--alt">
        <div className="container">
          <Reveal className="text-center" style={{ marginBottom: "48px" }}>
            <div className="tag" style={{ color: ACCENT, background: "rgba(14,165,233,.12)", borderColor: `${ACCENT}40` }}>
              Planos de Suporte
            </div>
            <h2 className="section-title">O que cada plano inclui</h2>
            <p className="section-subtitle" style={{ margin: "0 auto" }}>
              Os chamados são atendidos em três níveis de suporte, além do atendimento presencial —
              cada plano ST cobre um conjunto diferente de níveis.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card" style={{ padding: "8px", overflow: "hidden" }}>
              <div className="table-scroll">
                <table className="fin-table" style={{ minWidth: "680px" }}>
                  <thead>
                    <tr>
                      <th>Características</th>
                      {SUPPORT_TIERS.map((tier) => (
                        <th key={tier.id} style={{ textAlign: "center" }}>
                          {tier.label}
                          <br />
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: "11px",
                              color: "var(--text-muted)",
                              textTransform: "none",
                              letterSpacing: 0,
                            }}
                          >
                            {tier.name}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {SUPPORT_ROWS.map((row) =>
                      row.type === "group" ? (
                        <tr key={row.key}>
                          <td
                            colSpan={SUPPORT_TIERS.length + 1}
                            style={{
                              background: "var(--bg-alt)",
                              fontWeight: 700,
                              color: "var(--text-primary)",
                              fontSize: "13px",
                            }}
                          >
                            {row.title}
                            {row.note && (
                              <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: "12px" }}>
                                {" "}— {row.note}
                              </span>
                            )}
                          </td>
                        </tr>
                      ) : (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          {row.flags.map((flag, i) => (
                            <td key={i} style={{ textAlign: "center" }}>
                              {flag ? (
                                <span style={{ color: "var(--success)", fontWeight: 700 }}>✓</span>
                              ) : (
                                <span style={{ color: "var(--error)", fontWeight: 700 }}>✕</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="section">
        <div className="container" style={{ maxWidth: "780px" }}>
          <Reveal className="text-center" style={{ marginBottom: "40px" }}>
            <div className="tag" style={{ color: ACCENT, background: "rgba(14,165,233,.12)", borderColor: `${ACCENT}40` }}>
              FAQ
            </div>
            <h2 className="section-title">Dúvidas frequentes</h2>
          </Reveal>

          {FAQS.map((faq, index) => (
            <Reveal
              className={`faq-item${openFaq === index ? " open" : ""}`}
              delay={index * 0.05}
              key={faq.question}
            >
              <button
                className="faq-btn"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className="faq-btn__icon">+</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">
              Suporte que resolve,
              <br />
              não que enrola
            </h2>
            <p className="cta-banner__sub">
              Tenha uma equipe técnica disponível quando você precisar, sem
              custo por chamado.
            </p>
            <div className="cta-banner__actions">
              <Link to="/contato" className="btn btn-primary">
                Falar com a equipe →
              </Link>
              <Link to="/servicos/servicos" className="btn btn-outline">
                Ver todos os serviços
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
